"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.repCommitUploadSchema = exports.commitUploadSchema = exports.uploadStatusSchema = exports.uploadCreatedSchema = exports.uploadStateSchema = exports.UPLOAD_STATES = exports.TUS_PATCH_CONTENT_TYPE = exports.TUS_CHUNK_BYTES = exports.TUS_VERSION = void 0;
exports.uploadPhase = uploadPhase;
/**
 * The upload surface's wire types. Section 09.
 *
 * Here rather than in the backend because the `guest` frontend drives the whole
 * pipeline - create, append, poll - and section 18 makes `shared/contracts` the only
 * place a type crosses that boundary.
 *
 * WHAT IS NOT HERE
 *
 * Nothing about where the bytes went. No bucket, no object name, no storage ref, no
 * signed URL. Section 07 calls `storage_ref` opaque to the core, and it is more opaque
 * still to a browser: the repository is the customer's own system, and its object
 * layout is not something a prospect's browser should be able to describe.
 *
 * No `sha256` either. The hash is what the commit path verifies the bytes against, and
 * publishing it to the client would let a client that wanted to substitute bytes learn
 * what it had to match.
 *
 * The tus wire format itself is deliberately absent. tus is a header protocol - offsets
 * and lengths travel in `Upload-Offset` and `Upload-Length`, and the body of a `PATCH`
 * is raw bytes - so there are no zod schemas for those exchanges because there are no
 * JSON bodies. The constants below are the parts a client has to agree with us about.
 */
const zod_1 = require("zod");
/** The tus version this server implements. Section 09. */
exports.TUS_VERSION = '1.0.0';
/**
 * Section 09's chunk size, and what the client should use.
 *
 * Small enough that losing one on a bad connection costs little, large enough that a
 * 350MB upload is forty-four requests rather than thousands.
 */
exports.TUS_CHUNK_BYTES = 8 * 1024 * 1024;
/** The content type a tus `PATCH` body must declare. */
exports.TUS_PATCH_CONTENT_TYPE = 'application/offset+octet-stream';
/**
 * Section 09's state machine, as a client sees it.
 *
 * The same thirteen values the server uses, because a surface that collapsed them
 * would have to decide which distinctions matter - and the one that matters most,
 * `held` versus `rejected`, is exactly the one a simplifying map would lose. A held
 * document is coming back; a rejected one is not.
 */
exports.UPLOAD_STATES = [
    'created',
    'receiving',
    'staged',
    'scanning',
    'clean',
    'infected',
    'indeterminate',
    'committing',
    'committed',
    'rejected',
    'held',
    'failed',
    'expired',
];
exports.uploadStateSchema = zod_1.z.enum(exports.UPLOAD_STATES);
/**
 * The four things a surface actually renders, derived from the state.
 *
 * A convenience for the client and deliberately not what the server sends: the server
 * sends the state, and mapping it is a presentation decision. Exported so the three
 * frontends make the same one.
 */
function uploadPhase(state) {
    switch (state) {
        case 'created':
        case 'receiving':
            return 'sending';
        case 'staged':
        case 'scanning':
        case 'clean':
        case 'committing':
        case 'failed':
        case 'indeterminate':
        case 'held':
            // A hold shows as "checking" rather than as a failure. Section 09: both parties
            // see a checking state, and an operator can requeue it. Telling a prospect their
            // document was refused when it is queued for a second scan is worse than telling
            // them it is still being looked at.
            return 'checking';
        case 'committed':
            return 'done';
        case 'infected':
        case 'rejected':
        case 'expired':
            return 'stopped';
    }
}
/** What a tus creation request answers with, alongside the `Location` header. */
exports.uploadCreatedSchema = zod_1.z.object({
    uploadId: zod_1.z.string(),
    /** Section 09: 48 hours. Also sent as `Upload-Expires`. */
    expiresAt: zod_1.z.iso.datetime(),
    /** D-10's ceiling, echoed so a client need not have read the config. */
    maxObjectBytes: zod_1.z.number().int().positive(),
    chunkBytes: zod_1.z.number().int().positive(),
});
/** What the status endpoint renders, and what the commit request answers with. */
exports.uploadStatusSchema = zod_1.z.object({
    uploadId: zod_1.z.string(),
    state: exports.uploadStateSchema,
    /** Bytes the server holds. Authoritative, and what a resuming client continues from. */
    offset: zod_1.z.number().int().nonnegative(),
    /** Null when the client deferred its length. */
    expectedBytes: zod_1.z.number().int().nonnegative().nullable(),
    expiresAt: zod_1.z.iso.datetime(),
    /** Set once committed, so the surface can link to the document. */
    documentId: zod_1.z.string().nullable(),
    /**
     * Why an upload is held or was rejected, in words safe to show the person who sent
     * it. Null otherwise.
     *
     * A signature name is a fact about the file rather than a secret, and refusing a
     * document without saying why produces a support call.
     */
    detail: zod_1.z.string().nullable(),
});
/**
 * The commit request body. Section 13: `{ requestId?, name }`.
 *
 * `name` is what the document is called, which is not necessarily the filename the
 * browser had. Bounded because it is caller-controlled text that ends up in a
 * `Content-Disposition` header and in an audit payload.
 */
exports.commitUploadSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(255),
    /** The checklist item this fulfils, when the visitor was asked for something. */
    requestId: zod_1.z.uuid().optional(),
});
/**
 * The same, for a representative sending a document to a contact.
 *
 * `requestId` is deliberately absent rather than optional, and `strictObject` is what makes that mean
 * something: a `document_request` is the prospect's side of the exchange - the named thing the rep asked
 * *them* for - so a rep fulfilling one would resolve a checklist item nobody answered. A client sending
 * it is told, on section 15's reasoning about unknown configuration keys: silently dropping the field
 * would leave the caller believing an item had been ticked off.
 */
exports.repCommitUploadSchema = zod_1.z.strictObject({
    name: zod_1.z.string().min(1).max(255),
});
//# sourceMappingURL=upload.js.map